/**
 * Returns a promise that will resolve to `props` object populated with asynchronous props
 */
import { gql } from '@nerdwallet/apollo';
import getApolloClient from '@nerdwallet/apollo/client';
import { getLogger } from '@nerdwallet/logging';
import datadog from '@nerdwallet/datadog';

import { EXPERIMENT_IDS_TO_FETCH } from '../auth/lib/experiments';
import { extractPageTaxonomyValue } from './helpers';

const rootLogger = getLogger('fetch-compass-props');

const FETCH_IDENTITY = gql`
  query fetchIdentity {
    globalIdentityProfile {
      firstName
    }
  }
`;

const FETCH_FEATURES = gql`
  query fetchFeatureFlags {
    # Devnote: Add feature flags here
    # https://graphql.org/learn/queries/#aliases
    #
    # EXAMPLE:
    # myFeatureFlag: featureFlag(featureFlagName: "my_feature_flag") {
    #  ...featureFlagFields
    # }
    auth0Beta: featureFlag(featureFlagName: "auth0_beta") {
      ...featureFlagFields
    }
    savedArticlesMenuItem: featureFlag(featureFlagName: "saved_articles") {
      ...featureFlagFields
    }
    webPushNotificationsFeatureFlag: featureFlag(
      featureFlagName: "WebPushNotifications"
    ) {
      ...featureFlagFields
    }
    nerdWalletPlusActive: featureFlag(featureFlagName: "nerdwallet_plus") {
      ...featureFlagFields
    }
    creditBuilderLoanActive: featureFlag(
      featureFlagName: "credit_builder_loan"
    ) {
      ...featureFlagFields
    }
    nerdWalletPlusFeatureDiscovery: featureFlag(
      featureFlagName: "nwplus-feature-discovery"
    ) {
      ...featureFlagFields
    }
  }

  fragment featureFlagFields on FeatureFlag {
    name
    value
    extraData
  }
`;

const FETCH_EXPERIMENTS = gql`
  query BulkFetchExperimentDecisions(
    $testNames: [String!]!
    $userAttributes: JSON!
  ) {
    experimentDecisions(
      testNames: $testNames
      userAttributes: $userAttributes
    ) {
      ...ExperimentDecisionQueryFields
      __typename
    }
  }
  fragment ExperimentDecisionQueryFields on ExperimentDecision {
    experimentName
    assignedVariantName
    modelExecutionIds
    __typename
  }
`;

const FETCH_CREDIT_BUILDER_LOAN = gql`
  query GetUserLoanStatus {
    fetchLoanStatus {
      error
      loanStatus
    }
  }
`;

interface FetchHasCreditBuilderLoanProps {
  isLoggedIn: boolean;
  isCBLExperimentActive: boolean;
}

/**
 * Fetches whether a user has applied for a credit builder loan or not and there were no errors.
 * https://nerdwallet.atlassian.net/wiki/spaces/PEDD/pages/2843115579/Technical+Design+Doc+for+CBL
 * @returns {boolean}
 */
export const fetchHasCreditBuilderLoan = async ({
  isLoggedIn,
  isCBLExperimentActive,
}: FetchHasCreditBuilderLoanProps) => {
  const logger = getLogger('fetchCreditBuilderLoan', rootLogger);
  logger.info('fetchCreditBuilderLoan start');

  if (!isLoggedIn || !isCBLExperimentActive) {
    logger.info('fetchCreditBuilderLoan success');
    return false;
  }

  const apolloClient = await getApolloClient();

  try {
    const { data, error } = await datadog.instrumented(
      'fetchCreditBuilderLoan',
      {},
      () =>
        apolloClient.query({
          query: FETCH_CREDIT_BUILDER_LOAN,
        })
    );

    if (error) {
      throw error;
    }

    const hasCBLLoan =
      !error &&
      !data?.fetchLoanStatus?.error &&
      data?.fetchLoanStatus?.loanStatus !== 'LoanNotFound';

    logger.info('fetchCreditBuilderLoan success');
    return hasCBLLoan;
  } catch (e) {
    logger.error('fetchCreditBuilderLoan failed', e);
    return null;
  }
};

export const fetchIdentity = async () => {
  const logger = getLogger('fetchIdentity', rootLogger);

  const apolloClient = await getApolloClient();

  try {
    logger.info('fetchIdentity start');

    const { data, error } = await datadog.instrumented(
      'fetchIdentity',
      {},
      () =>
        apolloClient.query({
          query: FETCH_IDENTITY,
        })
    );

    if (error) {
      throw error;
    }

    logger.info('fetchIdentity success');
    return data?.globalIdentityProfile;
  } catch (e) {
    logger.info('fetchIdentity failed', e);
    return null;
  }
};

const fetchFeatureFlagsImpl = async () => {
  const logger = getLogger('fetchFeatureFlagsImpl', rootLogger);

  const apolloClient = await getApolloClient();

  try {
    logger.info('fetchFeatureFlagsImpl start');

    const { data, error } = await datadog.instrumented(
      'fetchFeatureFlagsImpl',
      {},
      () =>
        apolloClient.query({
          query: FETCH_FEATURES,
        })
    );

    if (error) {
      throw error;
    }

    logger.info('fetchFeatureFlagsImpl success');
    return data;
  } catch (e) {
    logger.error('fetchFeatureFlagsImpl failed', e);
    return null;
  }
};

export const fetchFeatureFlags = async () => {
  return fetchFeatureFlagsImpl();
};

const fetchExperiments = async (inputProps) => {
  const logger = getLogger('fetchExperimentsLogger', rootLogger);

  if (EXPERIMENT_IDS_TO_FETCH.length <= 0) {
    logger.info(`
      Experiment names are empty.
      You can add them by adding to EXPERIMENT_IDS_TO_FETCH in the experiments.ts file.
    `);
    return null;
  }

  const apolloClient = await getApolloClient();

  // User attributes to send to Optimizely in support of audience filtering.
  // https://nerdwallet.atlassian.net/browse/REG-1905
  const userAttributes = {
    vertical: extractPageTaxonomyValue(inputProps?.pageTaxonomy, 'vertical'),
    topic: extractPageTaxonomyValue(inputProps?.pageTaxonomy, 'topic'),
    content_type: extractPageTaxonomyValue(
      inputProps?.pageTaxonomy,
      'contentType'
    ),
  };

  try {
    logger.info('fetchExperiments start');

    const { data, error } = await datadog.instrumented(
      'fetchExperiments',
      {},
      () =>
        apolloClient.query({
          query: FETCH_EXPERIMENTS,
          variables: {
            testNames: EXPERIMENT_IDS_TO_FETCH,
            userAttributes,
          },
        })
    );

    if (error) {
      throw error;
    }

    logger.info('fetchExperiments success');
    return data;
  } catch (e) {
    logger.error('fetchExperiments failed', e);
    return null;
  }
};

export const fetchAsyncPropsClient = async (inputProps) => {
  const logger = getLogger('fetchAsyncPropsClient', rootLogger);

  try {
    const asyncPropPromises = await Promise.allSettled([
      fetchIdentity(),
      fetchFeatureFlags(),
      fetchExperiments(inputProps),
    ]);

    const asyncPropValues = asyncPropPromises.map((promise) =>
      promise.status === 'fulfilled' ? promise.value : {}
    );

    const [identity, featureFlags, experiments] = asyncPropValues;

    const hasCBLLoan = await fetchHasCreditBuilderLoan({
      isLoggedIn: Boolean(identity),
      isCBLExperimentActive: Boolean(
        featureFlags?.creditBuilderLoanActive?.value
      ),
    });

    return {
      identity,
      featureFlags,
      experiments,
      hasCBLLoan,
    };
  } catch (error) {
    logger.error('failed fetching async props', error);
    return {};
  }
};
